import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Role, ModulePermission, PermissionAction } from '../types/role';
import { api, authApi } from '../services/api.service';
import { toast } from 'sonner';
import { shouldSkipApiCalls } from '../utils/routeUtils';
import { useAuth } from '../context/AuthContext';

interface User {
  id: number;
  email: string;
  name?: string;
  roleId?: number;
  role?: Role;
  isSuperAdmin?: boolean;
}

interface RolePermissionContextType {
  currentUser: User | null;
  userPermissions: ModulePermission[];
  hasPermission: (module: string, action: PermissionAction) => boolean;
  hasAnyPermission: (module: string) => boolean;
  isSuperAdmin: boolean;
  loading: boolean;
  refreshPermissions: () => Promise<void>;
  user?: User | null;
}

const RolePermissionContext = createContext<RolePermissionContextType | undefined>(undefined);

export const useRolePermission = () => {
  const context = useContext(RolePermissionContext);
  if (!context) {
    throw new Error('useRolePermission must be used within RolePermissionProvider');
  }
  return context;
};

interface RolePermissionProviderProps {
  children: ReactNode;
}

export const RolePermissionProvider: React.FC<RolePermissionProviderProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userPermissions, setUserPermissions] = useState<ModulePermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  const fetchUserPermissions = async () => {
    console.log('🔍 RolePermissionContext: Starting permission fetch...', {
      isAuthenticated,
      authLoading,
      pathname: window.location.pathname
    });

    try {
      setLoading(true);

      // Only proceed if we're actually authenticated
      if (!isAuthenticated) {
        console.log('❌ RolePermissionContext: User not authenticated, skipping permission fetch');
        setCurrentUser(null);
        setUserPermissions([]);
        return;
      }

      // First, try to get current user from API
      try {
        console.log('🌐 RolePermissionContext: Fetching current user from API...');
        const authResponse = await authApi.getCurrentUser();
        if (authResponse && authResponse.admin) {
          const user = {
            id: authResponse.admin.id,
            email: authResponse.admin.email,
            isSuperAdmin: authResponse.admin.id === 1
          };

          console.log('✅ RolePermissionContext: User loaded from API:', {
            id: user.id,
            email: user.email,
            isSuperAdmin: user.isSuperAdmin
          });
          setCurrentUser(user);
          localStorage.setItem('user', JSON.stringify(user));
          localStorage.setItem('permissions_last_updated', Date.now().toString());
          
          // If super admin, grant all permissions
          if (user.isSuperAdmin || user.id === 1) {
            console.log('👑 RolePermissionContext: User is super admin, granting all permissions');
            const allPermissions: ModulePermission[] = [
              { module: 'dashboard', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
              { module: 'users', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
              { module: 'companies', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
              { module: 'candidates', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
              { module: 'jobs', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
              { module: 'jobcategory', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
              { module: 'jobroles', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
              { module: 'plans', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
              { module: 'settings', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
              { module: 'reports', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
              { module: 'contact', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
              { module: 'admin', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
              { module: 'role', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
              { module: 'orders', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
              { module: 'requests', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
              { module: 'attributes', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
            ];
            setUserPermissions(allPermissions);
            console.log('✅ RolePermissionContext: Set super admin permissions for', allPermissions.length, 'modules');
          } else {
            // For sub-admin, try to fetch permissions from API
            try {
              console.log('🌐 RolePermissionContext: Fetching permissions for sub-admin user:', user.id);
              const permissionsResponse = await api.role.getUserPermissions(user.id);
              console.log('✅ RolePermissionContext: Fetched permissions from API:', permissionsResponse);
              
              if (permissionsResponse && permissionsResponse.permissions && typeof permissionsResponse.permissions === 'object') {
                // Handle the response structure { permissions: { module: { action: boolean } } }
                const modulePermissions: ModulePermission[] = Object.entries(permissionsResponse.permissions).map(([module, perms]: [string, any]) => ({
                  module,
                  permissions: perms
                }));
                setUserPermissions(modulePermissions);
                console.log('Set user permissions from API:', modulePermissions);
              } else if (permissionsResponse && typeof permissionsResponse === 'object') {
                // Handle direct permissions object
                const modulePermissions: ModulePermission[] = Object.entries(permissionsResponse).map(([module, perms]: [string, any]) => ({
                  module,
                  permissions: perms
                }));
                setUserPermissions(modulePermissions);
                console.log('Set user permissions from API:', modulePermissions);
              } else {
                // If no permissions returned, show warning
                console.log('No permissions from API');
                setDefaultSubAdminPermissions();
              }
            } catch (permError) {
              console.error('Failed to fetch user permissions:', permError);
              // Don't set default permissions on error
              setDefaultSubAdminPermissions();
            }
          }
          
          return; // Successfully loaded from API
        }
      } catch (apiError) {
        console.log('❌ RolePermissionContext: Failed to get user from API:', {
          error: apiError instanceof Error ? apiError.message : 'Unknown error',
          status: (apiError as any)?.response?.status
        });
        // Don't throw error here as this might be due to invalid/expired auth
        // Fall back to localStorage or leave user as null
      }
      
      // Fallback to localStorage only if API call failed due to network issues, not auth issues
      const storedUser = localStorage.getItem('user');
      const permissionsLastUpdated = localStorage.getItem('permissions_last_updated');
      const PERMISSIONS_TTL = 5 * 60 * 1000; // 5 minutes to match backend cache

      // Check if cached permissions are still valid
      const isPermissionsCacheValid = permissionsLastUpdated &&
        (Date.now() - parseInt(permissionsLastUpdated)) < PERMISSIONS_TTL;

      if (!storedUser || !isPermissionsCacheValid) {
        console.log('No valid user cache in localStorage or permissions expired');
        setLoading(false);
        setCurrentUser(null);
        setUserPermissions([]);
        return;
      }

      const user = JSON.parse(storedUser);
      console.log('User loaded from localStorage (cache valid):', user);
      setCurrentUser(user);

      // If super admin, grant all permissions
      if (user.isSuperAdmin || user.id === 1) {
        // console.log('User is Super Admin - setting all permissions');
        const allPermissions: ModulePermission[] = [
          { module: 'dashboard', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
          { module: 'users', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
          { module: 'companies', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
          { module: 'candidates', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
          { module: 'jobs', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
          { module: 'jobcategory', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
          { module: 'jobroles', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
          { module: 'plans', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
          { module: 'settings', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
          { module: 'reports', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
          { module: 'contact', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
          { module: 'admin', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
          { module: 'role', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
          { module: 'orders', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
          { module: 'requests', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
          { module: 'attributes', permissions: { view: true, create: true, edit: true, delete: true, manage: true } },
        ];
        setUserPermissions(allPermissions);
      } else if (user.permissions) {
        // User already has permissions from login response
        const modulePermissions: ModulePermission[] = Object.entries(user.permissions).map(([module, perms]: [string, any]) => ({
          module,
          permissions: perms
        }));
        setUserPermissions(modulePermissions);
        console.log('Set user permissions from localStorage:', modulePermissions);
      } else {
        // Try to fetch permissions from API for sub-admin
        if (!user.isSuperAdmin && user.id) {
          try {
            const permissionsResponse = await api.role.getUserPermissions(user.id);
            console.log('Fetched permissions from API for sub-admin:', permissionsResponse);
            
            if (permissionsResponse && permissionsResponse.permissions && typeof permissionsResponse.permissions === 'object') {
              const modulePermissions: ModulePermission[] = Object.entries(permissionsResponse.permissions).map(([module, perms]: [string, any]) => ({
                module,
                permissions: perms
              }));
              setUserPermissions(modulePermissions);
            } else if (permissionsResponse && typeof permissionsResponse === 'object') {
              const modulePermissions: ModulePermission[] = Object.entries(permissionsResponse).map(([module, perms]: [string, any]) => ({
                module,
                permissions: perms
              }));
              setUserPermissions(modulePermissions);
            } else {
              setDefaultSubAdminPermissions();
            }
          } catch (error) {
            console.error('Failed to fetch permissions from API:', error);
            setDefaultSubAdminPermissions();
          }
        } else {
          setDefaultSubAdminPermissions();
        }
      }
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      // Only show toast error if user is authenticated but permissions failed
      // If auth failed, AuthContext will handle the redirect
      if (isAuthenticated) {
        toast.error('Failed to load user permissions');
      }
    } finally {
      setLoading(false);
      setIsInitialized(true);
    }
  };

  const setDefaultSubAdminPermissions = () => {
    // No default permissions - sub-admin must have permissions assigned through the role management system
    console.log('No default permissions set - permissions must be assigned through role management');
    setUserPermissions([]);
    toast.warning('No permissions assigned. Please contact your administrator.');
  };

  useEffect(() => {
    const currentPath = window.location.pathname;
    const shouldSkip = shouldSkipApiCalls();

    console.log('🚀 RolePermissionContext: useEffect triggered', {
      shouldSkipApiCalls: shouldSkip,
      authLoading,
      isAuthenticated,
      isInitialized,
      pathname: currentPath,
      href: window.location.href
    });

    // Skip permissions fetch on public pages to avoid unnecessary API calls
    // BUT: If user is authenticated and on a dashboard route, we need permissions
    if (shouldSkip && !isAuthenticated) {
      console.log('🚫 RolePermissionContext: Skipping permission fetch for public route (not authenticated)');
      setLoading(false);
      setIsInitialized(true);
      return;
    }

    // If user is authenticated but we're still on a public route, wait for navigation
    if (shouldSkip && isAuthenticated) {
      console.log('⚠️ RolePermissionContext: User authenticated but still on public route - may be mid-navigation');
    }

    // Wait for auth context to finish loading before fetching permissions
    if (authLoading) {
      console.log('⏳ RolePermissionContext: Waiting for auth context to finish loading...');
      return; // Still loading auth, wait
    }

    // If not authenticated, don't fetch permissions
    if (!isAuthenticated) {
      console.log('❌ RolePermissionContext: User not authenticated, clearing permissions');
      setLoading(false);
      setIsInitialized(true);
      setCurrentUser(null);
      setUserPermissions([]);
      return;
    }

    // Auth is complete and user is authenticated, now fetch permissions
    if (!isInitialized) {
      console.log('🔄 RolePermissionContext: Auth complete, fetching user permissions...');
      fetchUserPermissions();
    }
  }, [isInitialized, authLoading, isAuthenticated]);

  // Listen for route changes to re-fetch permissions when navigating from login to dashboard
  useEffect(() => {
    const handleLocationChange = () => {
      const currentPath = window.location.pathname;
      console.log('🔄 RolePermissionContext: Route changed to:', currentPath);

      // If we're now on a protected route and haven't initialized permissions yet
      if (!shouldSkipApiCalls() && isAuthenticated && !loading && isInitialized) {
        console.log('🔄 RolePermissionContext: Route changed to protected area, checking if permissions need refresh');
        // Only refresh if we don't have permissions yet
        if (userPermissions.length === 0) {
          console.log('🔄 RolePermissionContext: No permissions loaded, fetching now');
          setIsInitialized(false); // This will trigger a re-fetch
        }
      }
    };

    // Listen to popstate (back/forward) and custom navigation events
    window.addEventListener('popstate', handleLocationChange);

    // Also listen for pushstate/replacestate (programmatic navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(history, args);
      setTimeout(handleLocationChange, 0);
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(history, args);
      setTimeout(handleLocationChange, 0);
    };

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [isAuthenticated, loading, isInitialized, userPermissions.length]);

  const hasPermission = (module: string, action: PermissionAction): boolean => {
    if (currentUser?.isSuperAdmin || currentUser?.id === 1) return true;
    
    const modulePermission = userPermissions.find(p => p.module === module);
    const result = modulePermission ? modulePermission.permissions[action] || false : false;
    return result;
  };

  const hasAnyPermission = (module: string): boolean => {
    if (currentUser?.isSuperAdmin || currentUser?.id === 1) return true;
    
    const modulePermission = userPermissions.find(p => p.module === module);
    if (!modulePermission) return false;
    
    return Object.values(modulePermission.permissions).some(value => value === true);
  };

  const refreshPermissions = async () => {
    // Clear cache before refreshing
    localStorage.removeItem('permissions_last_updated');
    setIsInitialized(false);
    await fetchUserPermissions();
  };

  const value: RolePermissionContextType = {
    currentUser,
    userPermissions,
    hasPermission,
    hasAnyPermission,
    isSuperAdmin: currentUser?.isSuperAdmin || currentUser?.id === 1 || false,
    loading,
    refreshPermissions,
    user: currentUser,
  };

  return (
    <RolePermissionContext.Provider value={value}>
      {children}
    </RolePermissionContext.Provider>
  );
};